import { Component, Vue } from 'vue-property-decorator';
import { ITABLECOLCONF } from '@/types/base.d';
import exerciseApi, { removeExercise, updateExerciseSortOrder } from '@/api/exercise';

@Component
export default class exerciseTableMixin extends Vue {
  public cList: any = [];

  // 分组列表数据
  public groupedList: Array<{ type: number; typeLabel: string; questions: any[] }> = [];

  public chapterId: string = '';

  public loading: boolean = false;

  // 删除确认对话框状态（与课后作业保持一致）
  public showDeleteConfirm: boolean = false;

  public currentDeleteRow: any = null;

  // 排序相关状态
  public hasSortChanged: boolean = false;

  public savingSort: boolean = false;

  // 分组顺序：单选题(0)、判断题(1)、多选题(2)、填空题(3)、阅读题(5)、完善程序(7)、编程题(4)
  private readonly GROUP_ORDER = [0, 1, 2, 3, 5, 7, 4];

  public column: Array<ITABLECOLCONF> = [{
    label: '类型',
    prop: 'typeLabel',
    align: 'center',
    minWidth: '80px',
  }, {
    label: '题号',
    prop: 'questionId',
    align: 'center',
    minWidth: '60px',
  }, {
    label: '标题',
    prop: 'title',
    align: 'center',
    minWidth: '230px',
    tooltip: true,
  }, {
    label: '难度',
    prop: 'difficulty',
    align: 'center',
    minWidth: '60px',
  }, {
    label: '来源',
    prop: 'source',
    align: 'center',
    minWidth: '100px',
  }, {
    label: '地区',
    prop: 'area',
    align: 'center',
    minWidth: '80px',
  }, {
    label: '年份',
    prop: 'year',
    align: 'center',
    minWidth: '60px',
  }, {
    label: '操作',
    custom: true,
    minWidth: '120px',
    align: 'center',
    maxBtn: 1,
    btnList: [
      {
        title: '删除',
        name: '/course/info:remove',
        icon: 'trash-o',
        type: 'danger',
        callback: this.handleRemove,
        log: {
          name: '随堂练习 - 删除',
          component: '/course/info:remove',
        },
      },
    ],
  }]

  getData(pid: string): Promise<any[]> {
    return new Promise((resolve, reject) => {
      this.loading = true;
      exerciseApi.getExercisesByChapterPidWithQuestionDetails(pid).then((res: any) => {
        // 期望后端返回 { code, message, data: [...] }
        const list = (res && res.data) ? res.data : res;

        const typeMap: Record<number, string> = {
          0: '单选题',
          1: '判断题',
          2: '多选题',
          3: '填空题',
          4: '编程题',
          5: '阅读题',
          7: '完善程序',
        };

        // 处理数据，保留 bucketSort 字段
        const processed = (list || []).map((item: any) => {
          const q = item.question || {};
          const type = Number(q.type);
          return {
            ...q,
            exercisePid: item.pid, // 关系主键用于删除
            type,
            typeLabel: typeMap[type] || '未知类型',
            bucketSort: item.bucketSort || 0, // 保留排序字段
          };
        });

        // 按照分组规则进行分组
        const grouped: Record<number, any[]> = {};
        this.GROUP_ORDER.forEach((type) => {
          grouped[type] = [];
        });

        processed.forEach((item: any) => {
          if (grouped[item.type]) {
            grouped[item.type].push(item);
          }
        });

        // 每个分组内按照 bucketSort 排序
        this.GROUP_ORDER.forEach((type) => {
          if (grouped[type]) {
            grouped[type].sort((a, b) => (a.bucketSort || 0) - (b.bucketSort || 0));
          }
        });

        // 构建分组列表
        this.groupedList = this.GROUP_ORDER.map((type) => ({
          type,
          typeLabel: typeMap[type] || '未知类型',
          questions: grouped[type] || [],
        }));

        // 保持 cList 用于兼容（扁平化所有题目）
        this.cList = processed.sort((a, b) => {
          const typeOrderA = this.GROUP_ORDER.indexOf(a.type);
          const typeOrderB = this.GROUP_ORDER.indexOf(b.type);
          if (typeOrderA !== typeOrderB) {
            return typeOrderA - typeOrderB;
          }
          return (a.bucketSort || 0) - (b.bucketSort || 0);
        });

        this.chapterId = pid;
        this.hasSortChanged = false;
        this.loading = false;
        resolve(this.cList);
      }).catch((err: any) => {
        this.loading = false;
        reject(err);
      });
    });
  }

  handleRemove(row: any) {
    // 显示内联确认对话框（与课后作业一致）
    this.currentDeleteRow = row;
    this.showDeleteConfirm = true;
  }

  // 确认删除
  confirmDelete() {
    if (!this.currentDeleteRow) return;

    const row = this.currentDeleteRow;
    this.showDeleteConfirm = false;
    this.currentDeleteRow = null;

    if (!row || !row.exercisePid) {
      this.msg('记录信息缺失，无法删除', 'error');
      return;
    }

    removeExercise(row.exercisePid).then(() => {
      this.msg('删除成功', 'success', {
        duration: 3000,
        zIndex: 99999,
        position: 'top-center',
      });
      this.getData(this.chapterId);
    }).catch((error) => {
      console.error('删除失败:', error);
      this.msg('删除失败，请重试', 'error', {
        duration: 3000,
        zIndex: 99999,
        position: 'top-center',
      });
    });
  }

  // 取消删除
  cancelDelete() {
    this.showDeleteConfirm = false;
    this.currentDeleteRow = null;
  }

  // 处理分组内拖拽排序
  handleDragEnd(groupIndex: number) {
    this.hasSortChanged = true;
  }

  // 保存排序
  handleSaveSort() {
    if (!this.hasSortChanged || !this.chapterId) {
      return;
    }

    // 按照分组顺序和组内顺序收集所有题目的 pid
    const orderedQuestionIds: number[] = [];
    this.groupedList.forEach((group) => {
      group.questions.forEach((question) => {
        if (question.pid) {
          orderedQuestionIds.push(question.pid);
        }
      });
    });

    if (orderedQuestionIds.length === 0) {
      this.msg('没有可排序的题目', 'warning');
      return;
    }

    this.savingSort = true;
    updateExerciseSortOrder(this.chapterId, orderedQuestionIds)
      .then((res: any) => {
        this.msg(res.message || '排序保存成功', 'success', {
          duration: 3000,
          zIndex: 99999,
          position: 'top-center',
        });
        this.hasSortChanged = false;
        // 重新获取数据以更新 bucketSort
        this.getData(this.chapterId);
      })
      .catch((error: any) => {
        console.error('保存排序失败:', error);
        this.msg('保存排序失败，请重试', 'error', {
          duration: 3000,
          zIndex: 99999,
          position: 'top-center',
        });
      })
      .finally(() => {
        this.savingSort = false;
      });
  }
}


